import type { ReportResolver, ReportRow, Grain, ReportContext } from "@/lib/reports/types";
import { fetchBillingInputs } from "@/lib/billing-query";
import { calcBillingRatesData } from "@/lib/billing-rates-calc";

interface EmployeeCapacityInput {
  userId: string;
  name: string;
  kapasitet: number;
  brukt: number;
}

/**
 * Ren transform — ansatt-grain. Én rad per ansatt med periode-kapasitet,
 * loggede (brukte) timer og ledig kapasitet (kapasitet - brukt). Drillbar ned
 * til prosjekt (hvilke prosjekter den ansatte er tildelt timer på).
 */
export function kapasitetRows(data: EmployeeCapacityInput[]): ReportRow[] {
  return data.map((d) => ({
    id: d.userId,
    label: d.name,
    canDrill: true,
    cells: {
      kapasitet: d.kapasitet,
      brukt: d.brukt,
      ledig: d.kapasitet - d.brukt,
    },
  }));
}

/**
 * Per ansatt for valgt periode: kapasitet = sum(month.capacity), brukt =
 * sum(month.loggedHours), over periodens måneder. Samme billing-rates-kilde
 * som faktureringsgrad-resolveren.
 *
 * Kapasitet>0-filteret ekskluderer naturlig ledelse/eiere/test/eksterne
 * (monthly_capacity_hours = 0), så vi trenger ikke et eget ansatt-filter.
 *
 * KJENT BEGRENSNING (som faktureringsgrad): perioder som spenner to kalenderår
 * bruker kun året fra `from`; måneder i `to`-året utelates til kryss-år-
 * semantikken koordineres på rammeverk-nivå.
 */
async function fetchKapasitet(ctx: ReportContext): Promise<EmployeeCapacityInput[]> {
  const year = new Date(ctx.period.from).getFullYear();
  const fromKey = ctx.period.from.slice(0, 7); // yyyy-mm
  const toKey = ctx.period.to.slice(0, 7);

  const inputs = await fetchBillingInputs({ year });
  const { employees } = calcBillingRatesData({ ...inputs, year, now: new Date() });

  return employees
    .map((emp) => {
      let kapasitet = 0;
      let brukt = 0;
      for (const m of emp.months) {
        const key = `${m.year}-${String(m.month).padStart(2, "0")}`;
        if (key < fromKey || key > toKey) continue;
        kapasitet += m.capacity;
        brukt += m.loggedHours;
      }
      return { userId: emp.userId, name: emp.fullName, kapasitet, brukt };
    })
    .filter((e) => e.kapasitet > 0);
}

export const kapasitetResolver: ReportResolver = {
  async fetch(level: Grain, ctx: ReportContext): Promise<ReportRow[]> {
    if (level === "ansatt") {
      return kapasitetRows(await fetchKapasitet(ctx));
    }
    // TODO: prosjekt drill-down (per-prosjekt tildelte timer for valgt ansatt).
    return [];
  },
};
